import React, { useState, useEffect } from "react";
import { Chart as ChartJS, ArcElement, Tooltip, Legend } from "chart.js";
import { Pie } from "react-chartjs-2";
import { Button, Card, Spinner } from "react-bootstrap";
import { fetchReportPrograms, fetchPrograms } from "../../../services/httpClient";
import { loadingCard } from "../../../helpers/loading";

ChartJS.register(ArcElement, Tooltip, Legend);

interface RawDataItem {
  level: string;
  level_id?: string | number;
  program: string;
  program_id?: string | number;
}

interface ProgramPieChartProps {
  data?: RawDataItem[];
}

export const ProgramPieChart = ({ data }: ProgramPieChartProps) => {
  // Only program view
  const [fetchedData, setFetchedData] = useState<RawDataItem[] | null>(null);

  // Fetch data if not provided
  useEffect(() => {
    if (!data) {
      fetchReportPrograms()
        .then((res: any) => {
          const progData = res[0]?.data ?? [];
          const sortedProgData = [...progData].sort((a: any, b: any) => {
            if (a.program < b.program) return -1;
            if (a.program > b.program) return 1;
            if (!a.level_order) return 1;
            if (!b.level_order) return -1;
            if (a.level_order < b.level_order) return -1;
            if (a.level_order > b.level_order) return 1;
            return 0;
          });

          fetchPrograms()
            .then((res: any) => {
              const programs = res[0]?.data ?? [];

              programs.forEach((prog: any) => {
                const levels = prog.programLevels ?? [];
                // Add level name to each programData entry
                sortedProgData.forEach((d: any) => {
                  if (d.program_id === prog.id) {
                    const level = levels.find((l: any) => l.id === d.level_id);
                    d.level = level?.name;
                  }
                });
              });
              setFetchedData(sortedProgData);
            })
            .catch((err: any) => console.log(err));
        })
        .catch((err: any) => console.log(err));
    } else {
      data.forEach((d: any) => {});
    }
  }, [data]);

  const chartSource = data ?? fetchedData ?? [];
  const programGroups = groupByProgram(chartSource);

  function groupByProgram(data: RawDataItem[]) {
    const counts: Record<string, { name: string; count: number }> = {};
    data.forEach((item) => {
      const key = item.program_id ?? item.program;
      const name = item.program ? item.program.toUpperCase() : "NONE";
      if (!counts[key]) {
        counts[key] = { name, count: 0 };
      }
      counts[key].count += 1;
    });
    return Object.entries(counts).map(([id, { name, count }]) => ({ id, label: name, numberCases: count }));
  }

  const chartData = {
    labels: programGroups.length > 0 ? programGroups.map((item) => item.label) : ["No data available"],
    datasets: [
      {
        label: "# People",
        data: programGroups.length > 0 ? programGroups.map((item) => item.numberCases) : [0],
        backgroundColor: ["#ed2f2f", "#DA7E39", "#B2BEB5", "#5A7082", "#5D82A0", "#F4A259", "#B5B2A4", "#A0C1B8", "#E07A5F", "#3D5A80"],
        borderWidth: 1,
      },
    ],
  };

  const options = {
    plugins: {
      title: {
        display: true,
        text: "Program Assignment",
        color: "#5a7082",
        font: {
          size: 18,
          weight: 500,
        },
      },
      legend: {
        display: true,
        position: "left" as const,
        align: "center" as const,
        labels: {
          boxWidth: 25,
          font: {
            size: 14,
          },
          padding: 15,
        },
      },
    },
    layout: {
      padding: {
        top: 0,
        bottom: 0,
      },
    },
  };

  if (!chartSource.length) {
    return loadingCard();
  }

  return (
    <div style={{ height: 350, display: "flex", alignItems: "center", justifyContent: "center" }}>
      <Pie data={chartData} options={options} width={350} height={350} style={{ maxHeight: "100%", maxWidth: "100%" }} />
    </div>
  );
};
